import { useCallback, useMemo } from 'react'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { Chart } from '../components/Chart.js'
import type { ChartProps } from '../types/index.js'
import { AnalyticsFeeder } from '../utils/analyticsFeeder.js'
import { SCORE_SELECT_QUERY } from '../utils/constants.js'
import type { AnalyticsQuery } from '../utils/generateAnalyticsQuery.js'
import {
  generateAnalyticsQuery,
  generateTimeFrame,
} from '../utils/generateAnalyticsQuery.js'
import { getValueFromAnalyticsData } from '../utils/parser.js'

export const MemberScore = ({
  scope,
  id,
  timeFrame,
  className,
  segment,
}: ChartProps) => {
  const { $t } = useI18n()

  const createQueries = useCallback(() => {
    const generalQuery: AnalyticsQuery = {
      selects: [],
      timeFrame: generateTimeFrame({ timeFrame }),
      where: [
        { field: scope, value: id },
        { field: 'space_type', in: ['GROUP', 'BROADCAST'] },
        ...(segment?.where ? [segment.where] : []),
      ],
      limit: timeFrame.xAxisCount + 2,
    }

    const currentQuery: AnalyticsQuery = {
      ...generalQuery,
      selects: [
        {
          field: SCORE_SELECT_QUERY,
          as: 'Score',
        },
      ],
    }

    const prevQuery: AnalyticsQuery = {
      ...currentQuery,
      timeFrame: generateTimeFrame({ timeFrame, target: 'previous' }),
    }

    if (timeFrame.id === 'allTime') {
      return [generateAnalyticsQuery(currentQuery)]
    }

    return [
      generateAnalyticsQuery(currentQuery),
      generateAnalyticsQuery(prevQuery),
    ]
  }, [id, scope, timeFrame, segment])

  const queries = useMemo(() => [createQueries()], [createQueries])

  return (
    <AnalyticsFeeder queries={queries[0]}>
      {({ data, isLoading }) => (
        <Chart
          type="Score"
          title={$t({
            id: 'Generics.Score',
            defaultMessage: 'Score',
          })}
          description={timeFrame.text}
          current={getValueFromAnalyticsData(data[0], 'Score')}
          previous={getValueFromAnalyticsData(data[1], 'Score')}
          isLoading={isLoading}
          className={className}
        />
      )}
    </AnalyticsFeeder>
  )
}
